import { useState, useEffect } from 'react';
import {
  Theme,
  IconStyle,
  useTranslation,
  useBreakpoint,
  useSession,
  useNextgenRouter,
  Menu,
  IconButton,
  Modal,
  ButtonAlignment,
  Breakpoints,
  type MenuItem,
} from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import {
  CartRoutePaths,
  ListResponse,
  ListType,
  UpdateListRequest,
  myListApi,
  getListCountConfig,
  CartConstantsEnum,
  ErrorAPI,
} from '@commerce-cart-webcomponents/sdk';
import { CreateListDrawer } from '../create-list-drawer/CreateListDrawer';
import { useCartNotification } from '../../../../common/hooks/useCartNotification';
import { CartNotificationTheme } from '../../../../common/components/cart-notification/types/enums';
import { TOAST_TIMEOUT } from '../../../../common/types/enums';
import { ModalWrapper, useExceptionToast } from '../../../../common';
import styles from './ListMenu.module.scss';
import { ListMenuPropTypes } from './types/propTypes';

export const ListMenu = ({
  listDetails,
  onListUpdate,
  showInMyShop = false,
  showInListDetails = false,
  showLoaderCB,
  customClass = '',
}: ListMenuPropTypes) => {
  const t = useTranslation();
  const viewport = useBreakpoint();
  const { isABO } = useSession();
  const router = useNextgenRouter();
  const deleteList = t('cart.deleteList.deleteList');
  const displayOnMyShopPrivate = t('cart.displayOnMyShop.displayOnMyShopPrivate');
  const displayLists = t('cart.displayOnMyShop.displayList');
  const removeLists = t('cart.removeFromOnMyShop.removeList');
  const yesDelete = t('cart.deleteList.yesDelete');
  const cancel = t('cart.addToListdrawer.cancel');
  const removeFromMyShop = t('cart.createListDrawer.removeFromMyShop');
  const displayOnMyShop = t('cart.createListDrawer.displayOnMyShop');
  const disableSharing = t('cart.createListDrawer.disableSharing');
  const enableSharing = t('cart.createListDrawer.enableSharing');
  const enableSharingPrivate = t('cart.enableDisable.private');
  const disabledSharingShared = t('cart.enableDisable.shared');
  const listPrivacyUpdated = t('cart.enableDisable.listPrivacy');
  const currentViewport = viewport === Breakpoints.XS ? ButtonAlignment.VERTICAL : ButtonAlignment.HORIZONTAL;
  const deleteShared = listDetails.listType === ListType.Shareable ? t('cart.deleteList.shared') : '';
  const displayMyShopShared = listDetails.listType === ListType.Shareable ? t('cart.displayOnMyShop.shareable') : '';
  const deleteModalBody = listDetails.isDisplayedOnShop ? t('cart.deleteList.displayedOnMyShop') : deleteShared;
  const displayOnMyShopModalBody =
    listDetails.listType === ListType.Private ? t('cart.displayOnMyShop.private') : displayMyShopShared;
  const listSetupText = t('cart.displayOnMyShop.listSetup');
  const enableSharingAnytime = t('cart.enableDisable.enableSharingAnytime');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDisplayOnMyShopModal, setShowDisplayOnMyShopModal] = useState(false);
  const [showEnableDisableModal, setShowEnableDisableModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showListDrawer, setShowListDrawer] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isRemove, setIsRemove] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const deleteListMessage = t('cart.deleteList.deleteSuccess');
  const enableDisabledHeading = isShared ? disableSharing : enableSharing;
  const isListEmpty = listDetails.payLoad?.entries?.length === 0;
  const memberItemsCheck = Number(listDetails?.maxCount) >= Number(getListCountConfig()) || isListEmpty;
  const { createToast, deleteToast } = useCartNotification(isEdit ? 'edit-list' : 'create-list');
  const triggerToast = useExceptionToast();
  const memberMenuItems: MenuItem[] = memberItemsCheck
    ? [
        {
          id: 'edit-list',
          title: t('cart.createListDrawer.editList'),
          onClick: () => editListAction(),
        },
        {
          id: 'delete-list',
          title: t('cart.createListDrawer.deleteList'),
          onClick: () => deleteListAction(),
        },
      ]
    : [
        {
          id: 'edit-list',
          title: t('cart.createListDrawer.editList'),
          onClick: () => editListAction(),
        },
        {
          id: 'duplicate-list',
          title: t('cart.createListDrawer.duplicateList'),
          onClick: () => duplicateListAction(),
        },
        {
          id: 'delete-list',
          title: t('cart.createListDrawer.deleteList'),
          onClick: () => deleteListAction(),
        },
      ];
  const [menuItems, setMenuItems] = useState<MenuItem[]>(memberMenuItems);
  const onSuccess = (res?: ListResponse) => {
    if (res) {
      onListUpdate(res);
    }
    setShowListDrawer(false);
  };
  const editListAction = () => {
    setIsEdit(true);
    setShowMenu(false);
    setShowListDrawer(true);
  };
  const duplicateListAction = () => {
    setIsEdit(false);
    setShowMenu(false);
    setShowListDrawer(true);
  };
  const deleteListAction = () => {
    setShowMenu(false);
    setShowDeleteModal(true);
  };
  const displayOnMyShopAction = () => {
    setIsRemove(listDetails.isDisplayedOnShop ?? false);
    setShowMenu(false);
    setShowDisplayOnMyShopModal(true);
  };
  const enableDisabledAction = () => {
    setIsShared(listDetails.listType === ListType.Shareable);
    setShowMenu(false);
    setShowEnableDisableModal(true);
  };
  const deleteListHandler = () => {
    if (listDetails?.listId) {
      myListApi
        .deleteList(listDetails?.listId)
        .then(() => {
          if (showInMyShop) {
            // From MyShop Cards we dont need to go anywhere just update the lists.
            onListUpdate(listDetails, true);
          } else if (showInListDetails) {
            router.back();
          } else {
            router.push(CartRoutePaths.LIST);
          }
          createToast(
            {
              theme: CartNotificationTheme.SUCCESS,
              rightSeperator: false,
              content: deleteListMessage,
              clickIcon: 'times',
              icon: 'check-circle',
              timeOut: TOAST_TIMEOUT,
            },
            deleteToast,
          );
          setShowDeleteModal(false);
        })
        .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps));
    }
  };
  const updateListHandler = async (_listDetails: UpdateListRequest & { listId: string }) => {
    const res = await myListApi.updateList([_listDetails]);
    setShowMenu(false);
    if (res.lists?.length) {
      onListUpdate(res.lists[0], false, !_listDetails.isDisplayedOnShop);
    }
  };

  const updateDisplayOnMyShop = () => {
    showLoaderCB?.(true);
    const _listDetails = listDetails;
    if (_listDetails?.listId) {
      updateListHandler({
        listId: _listDetails.listId,
        name: _listDetails.payLoad?.name ?? '',
        description: _listDetails.payLoad?.description ?? '',
        category: _listDetails.category ?? '',
        isDisplayedOnShop: !_listDetails.isDisplayedOnShop,
        listType: _listDetails.listType ?? ListType.Private,
        version: _listDetails?.version ?? 0,
      })
        .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps))
        .finally(() => showLoaderCB?.(false));
    }
    setShowDisplayOnMyShopModal(false);
  };
  const enableDisableSharing = () => {
    showLoaderCB?.(true);
    const _listDetails = listDetails;
    if (_listDetails?.listId) {
      updateListHandler({
        listId: _listDetails.listId,
        name: _listDetails.payLoad?.name ?? '',
        description: _listDetails.payLoad?.description ?? '',
        category: _listDetails.category ?? '',
        isDisplayedOnShop: _listDetails.isDisplayedOnShop ?? false,
        listType: _listDetails.listType === ListType.Shareable ? ListType.Private : ListType.Shareable,
        version: _listDetails?.version ?? 0,
      })
        .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps))
        .finally(() => showLoaderCB?.(false));
    }
    createToast(
      {
        theme: CartNotificationTheme.SUCCESS,
        rightSeperator: false,
        content: listPrivacyUpdated,
        clickIcon: 'times',
        icon: 'check-circle',
        timeOut: TOAST_TIMEOUT,
      },
      deleteToast,
    );
    setShowEnableDisableModal(false);
  };
  const isMyShop = () => router.pathname?.split('/').includes('myshop');
  const myShopExists = StorageService.getLocalStorage().retrieve(CartConstantsEnum.MyShopExists);

  useEffect(() => {
    setMenuItems(memberMenuItems);
    const aboMenuItemsSharing = [
      {
        id: 'enable-sharing',
        title: listDetails.listType === ListType.Shareable ? disableSharing : enableSharing,
        onClick: () => enableDisabledAction(),
      },
    ];
    const aboAllMenuItems = [
      {
        id: 'display-on-my-shop',
        title: listDetails.isDisplayedOnShop ? removeFromMyShop : displayOnMyShop,
        onClick: () => displayOnMyShopAction(),
      },
      ...aboMenuItemsSharing,
    ];

    let value = aboAllMenuItems;
    if (isListEmpty && !listDetails.isDisplayedOnShop) {
      value = aboMenuItemsSharing;
    }
    if (!isABO()) {
      value = aboMenuItemsSharing;
    }
    if (isABO()) {
      setMenuItems([...value, ...memberMenuItems]);
    }
  }, [listDetails]);
  return (
    <>
      {listDetails?.listType !== ListType.Wishlist && (
        <div className={`${styles['myLists-menu-container']} ${styles[customClass]}`}>
          <div className={styles['icon-container']}>
            <IconButton
              iconName={'ellipsis-vertical'}
              theme={Theme.BLACK}
              iconStyle={IconStyle.SOLID}
              iconWithBorder={true}
              containerClassName={`${styles['myLists-icon']} ${showInMyShop && styles['myShop']}`}
              onClick={() => setShowMenu(!showMenu)}
              data-testId="menu-icon"
            />
          </div>
          {showMenu && (
            <Menu
              className={`${styles['myLists-menu']} ${showInMyShop && styles['myShop']}`}
              menuItems={menuItems}
              onClose={() => setShowMenu(false)}
            />
          )}
        </div>
      )}
      {showListDrawer && (
        <CreateListDrawer
          showLoaderCB={showLoaderCB}
          isEdit={isEdit}
          listDetails={listDetails}
          onDrawerClose={() => setShowListDrawer(false)}
          onSuccessAction={onSuccess}
        />
      )}
      {showDeleteModal && (
        <Modal
          headline={deleteList}
          primaryButtonLabel={yesDelete}
          secondaryButtonLabel={cancel}
          buttonAlignment={currentViewport}
          closeIcon={true}
          closeOnBackdropClick={true}
          onClose={() => setShowDeleteModal(false)}
          onPrimaryButtonClick={() => deleteListHandler()}
          onSecondaryButtonClick={() => setShowDeleteModal(false)}
        >
          {<>{isABO() && deleteModalBody}</>}
        </Modal>
      )}
      {showDisplayOnMyShopModal && !myShopExists && (
        <ModalWrapper
          headLineText={t('cart.listMenu.createMyShop')}
          description={t('cart.listMenu.createMyShopModalDesc')}
          primaryButtonLabel={t('cart.listMenu.createMyShop')}
          secondaryButtonLabel={t('cart.listMenu.cancel')}
          onPrimarySuccess={() => router.push(CartRoutePaths.MYSHOP)}
          onSecondarySuccess={() => setShowDisplayOnMyShopModal(false)}
        />
      )}
      {showDisplayOnMyShopModal && myShopExists && (
        <Modal
          headline={isRemove ? removeFromMyShop : displayOnMyShopPrivate}
          primaryButtonLabel={isRemove ? removeLists : displayLists}
          secondaryButtonLabel={cancel}
          buttonAlignment={currentViewport}
          closeIcon={true}
          closeOnBackdropClick={true}
          onClose={() => setShowDisplayOnMyShopModal(false)}
          onPrimaryButtonClick={() => updateDisplayOnMyShop()}
          onSecondaryButtonClick={() => setShowDisplayOnMyShopModal(false)}
        >
          <>
            {isRemove ? t('cart.removeFromOnMyShop.shareable') : displayOnMyShopModalBody}
            {isMyShop() && <p className={styles['list-setup']}>{listSetupText}</p>}
          </>
        </Modal>
      )}
      {showEnableDisableModal && (
        <Modal
          headline={enableDisabledHeading}
          primaryButtonLabel={enableDisabledHeading}
          secondaryButtonLabel={cancel}
          buttonAlignment={currentViewport}
          closeIcon={true}
          closeOnBackdropClick={true}
          onClose={() => setShowEnableDisableModal(false)}
          onPrimaryButtonClick={() => enableDisableSharing()}
          onSecondaryButtonClick={() => setShowEnableDisableModal(false)}
        >
          <>
            {isShared ? disabledSharingShared : enableSharingPrivate}
            {isShared && <p className={styles['list-setup']}>{enableSharingAnytime}</p>}
          </>
        </Modal>
      )}
    </>
  );
};
